import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, Linkedin, Twitter, Facebook, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NewsletterSection from '../components/NewsletterSection';
import { ScrollReveal } from '../components/ScrollAnimations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/blog/articles/${slug}`);
      if (!response.ok) throw new Error('Article not found');
      const data = await response.json();
      setArticle(data);
      
      // Fetch related articles from same category
      const relatedResponse = await fetch(`${API_URL}/api/blog/articles?category=${data.category}&limit=3`);
      const relatedData = await relatedResponse.json();
      setRelatedArticles(relatedData.articles.filter(a => a.slug !== slug).slice(0, 2));
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = article?.title || '';

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen">
        <Header />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="bg-black min-h-screen">
        <Header />
        <div className="flex flex-col justify-center items-center min-h-[60vh] text-center px-6">
          <h1 className="text-3xl font-bold text-white mb-4">Article non trouvé</h1>
          <p className="text-white/60 mb-8">L'article que vous recherchez n'existe pas ou a été supprimé.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-accent hover:underline"
          >
            <ArrowLeft size={16} />
            Retour au blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Article Header */}
      <section className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <ScrollReveal animation="fadeUp">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-accent transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              Retour au blog
            </Link>
            
            <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium mb-4">
              {article.category}
            </span>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-white/50 text-sm mb-8">
              <span className="flex items-center gap-2">
                <User size={16} />
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(article.published_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={16} />
                {article.read_time} min de lecture
              </span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Featured Image */}
      {article.image && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <ScrollReveal animation="fadeUp" delay={100}>
              <div className="aspect-video bg-white/5 overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-[1fr_auto] gap-12">
            {/* Main Content */}
            <ScrollReveal animation="fadeUp" delay={200}>
              <article 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:font-bold prose-headings:text-white
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-white/70 prose-p:leading-relaxed
                  prose-a:text-accent prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white
                  prose-ul:text-white/70 prose-ol:text-white/70
                  prose-li:marker:text-accent
                  prose-blockquote:border-l-accent prose-blockquote:text-white/60 prose-blockquote:italic
                  prose-code:text-accent prose-code:bg-white/5 prose-code:px-2 prose-code:py-1 prose-code:rounded
                  prose-pre:bg-white/5 prose-pre:border prose-pre:border-white/10"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </ScrollReveal>

            {/* Sidebar - Share */}
            <div className="lg:w-16">
              <div className="lg:sticky lg:top-32">
                <ScrollReveal animation="fadeLeft" delay={300}>
                  <div className="flex lg:flex-col items-center gap-4 p-4 bg-white/[0.02] border border-white/10">
                    <span className="text-white/40 text-xs uppercase tracking-wider lg:-rotate-90 lg:mb-4">
                      Partager
                    </span>
                    <a 
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-white/5 text-white/50 hover:bg-accent hover:text-black transition-all"
                    >
                      <Linkedin size={18} />
                    </a>
                    <a 
                      href={shareLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-white/5 text-white/50 hover:bg-accent hover:text-black transition-all"
                    >
                      <Twitter size={18} />
                    </a>
                    <a 
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-white/5 text-white/50 hover:bg-accent hover:text-black transition-all"
                    >
                      <Facebook size={18} />
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <ScrollReveal animation="fadeUp">
              <h2 className="text-2xl font-bold text-white mb-8">Articles similaires</h2>
            </ScrollReveal>
            
            <div className="grid md:grid-cols-2 gap-8">
              {relatedArticles.map((related, index) => (
                <ScrollReveal key={related.id} animation="fadeUp" delay={index * 100}>
                  <Link 
                    to={`/blog/${related.slug}`}
                    className="group block"
                  >
                    <article className="bg-white/[0.02] border border-white/10 hover:border-accent/30 transition-all p-6">
                      <span className="text-accent text-xs font-medium">{related.category}</span>
                      <h3 className="text-lg font-semibold text-white mt-2 mb-3 group-hover:text-accent transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-white/50 text-sm line-clamp-2">{related.excerpt}</p>
                    </article>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <NewsletterSection />

      <Footer />
    </div>
  );
};

export default ArticlePage;
